import { z } from 'zod';
import {
  extractedDrawingKindSchema,
  extractedDrawingStoreyKindSchema,
  extractedPlanViewSchema,
  northMethodSchema,
} from '../models/project-file-drawing.model';

const nullableStr = (description: string) =>
  z.nullable(z.string()).describe(description);

/** OpenAI structured output name for drawing region detection. */
export const PROJECT_FILE_DRAWING_DETECTION_SCHEMA_NAME =
  'project_file_drawing_detection' as const;

const drawingDetectionItemSchema = z.object({
  page: z
    .number()
    .int()
    .describe('1-based page number in the uploaded file. Use 1 for images.'),
  bbox_left: z
    .number()
    .describe('Left edge of the drawing as a fraction of page width (0–1).'),
  bbox_top: z
    .number()
    .describe('Top edge of the drawing as a fraction of page height (0–1).'),
  bbox_width: z
    .number()
    .describe('Width of the drawing as a fraction of page width (0–1).'),
  bbox_height: z
    .number()
    .describe('Height of the drawing as a fraction of page height (0–1).'),
  building_instance_id: nullableStr(
    'First id in `building_instance_ids`, or null when that list is empty or has several ids. The list is the attachment, not this field.',
  ),
  building_instance_ids: z
    .array(z.string())
    .describe(
      "Classifier slugs for every building or part this drawing shows. Several when one plan covers several parts of the same building. Empty when the drawing is not tied to a named building — do not invent a slug from the project name. A crop that shows one part uses that part's hinted id, matched from the stamp. A plan of one connected volume lists every hinted id the sheet shows. Do not copy the first hinted id onto every crop.",
    ),
  building_label: nullableStr(
    'In-document name (e.g. "Hus 1"); null if absent.',
  ),
  drawing_kind: extractedDrawingKindSchema.describe(
    'plan = looking down at a level; section = cut; elevation = fasad; other = details, site insets, title-only.',
  ),
  plan_view: z
    .nullable(extractedPlanViewSchema)
    .describe(
      'What a plan shows. floor_plan = a storey drawn as construction: walls, rooms, doors and windows. area_diagram = a storey shown as filled or outlined areas for an area calculation (BTA, BRA, BOA), with little or no construction drawn. site_plan = the site seen from above, with the buildings as outlines among streets, plots and neighbours (situationsplan). Null for a section, an elevation or other.',
    ),
  storey_kind: extractedDrawingStoreyKindSchema.describe(
    'Role of a plan. entrance = the entrance level (entréplan, bottenplan, or the plan directly above a garage or cellar). top = the uppermost floor only. A printed plan number is not top unless that floor is the top floor, and a plan number is not the entrance. typical = an intermediate floor, including a numbered plan that has its own sheet and is not the entrance, not the uppermost floor, and not below ground. That role does not mean the sheet covers other floors. roof = takplan: the roof seen from above, with no rooms on it. A plan that shows rooms on a roof (a technical room, a stair head) is a storey of its own, top when it is the uppermost, and its printed plan number names that storey. cellar = garage or källare, below ground. courtyard = gårdsplan. unknown for a section, an elevation, and a plan whose role the sheet does not support.',
  ),
  storey_kind_per_building: z
    .array(
      z
        .object({
          building_instance_id: z
            .string()
            .describe('One id from `building_instance_ids`.'),
          storey_kind: extractedDrawingStoreyKindSchema.describe(
            'Role of this plan for that building, with the same values as `storey_kind`.',
          ),
        })
        .strict(),
    )
    .describe(
      "Only for a plan that shows several buildings when the level it shows has a different role for each of them, for example one building's courtyard level that is another building's entrance level. One row per id in `building_instance_ids`. Empty otherwise.",
    ),
  plan_numbers: z
    .array(z.number().int())
    .describe(
      'Plan numbers printed on this sheet, ascending. "Plan 3" → [3]; "Plan 11-14" → [11, 12, 13, 14]; "PLAN 09, 10" → [9, 10]. A printed number means this sheet is that storey\'s own drawing. Empty when none are printed. A number in the file name is not a printed plan number. Empty for sections and elevations.',
    ),
  view_label: nullableStr(
    'Printed name of the drawing (e.g. "Plan 13", "Entréplan", "Sektion A–A"). Not a substitute for plan_numbers.',
  ),
  scale: nullableStr(
    'Scale as printed. When the stamp prints a paper size, include it in this string whether it sits in the scale parenthesis or in the stamp format field (for example "1:100 (A3)"). Do not read the paper size from the file name. Null when no scale is printed.',
  ),
  drawing_id: nullableStr('Ritningsnummer / drawing number from the stamp.'),
  north_method: northMethodSchema
    .extract(['north_arrow', 'coordinate_grid', 'none'])
    .describe(
      'How this drawing shows north. north_arrow = a north arrow or compass rose (norrpil) printed for it, also one elsewhere on the sheet that serves every drawing on it. coordinate_grid = map coordinates or a coordinate grid printed on it, with no arrow. none = nothing on the sheet shows north; a drawing being upright on the page does not.',
    ),
  north_deg: z
    .nullable(z.number())
    .describe(
      'Degrees clockwise from straight up on the page to north on this drawing, as `north_method` shows it (0 = north is up, 90 = north points right). Null when `north_method` is none.',
    ),
  scale_bar: z
    .nullable(
      z
        .object({
          zero_x: z
            .number()
            .describe("The bar's 0 tick, as a fraction of page width (0–1)."),
          zero_y: z
            .number()
            .describe("The bar's 0 tick, as a fraction of page height (0–1)."),
          end_x: z
            .number()
            .describe(
              'The farthest tick that prints a length, as a fraction of page width (0–1).',
            ),
          end_y: z
            .number()
            .describe(
              'The farthest tick that prints a length, as a fraction of page height (0–1).',
            ),
          end_m: z
            .number()
            .describe(
              'The length that tick prints, in metres ("50m" → 50, "20" next to a metre bar → 20).',
            ),
        })
        .strict(),
    )
    .describe(
      'The graphic scale bar (skalstock) printed for this drawing: a ruler with ticks labelled in metres. Place the points on the tick marks themselves, not on the labels. When one bar on the sheet serves every drawing at that scale, give it for each of them. Null when no scale bar is printed. A printed ratio like 1:500 is not a scale bar.',
    ),
  dimensions: z
    .array(
      z
        .object({
          start_x: z
            .number()
            .describe(
              "Where the dimension's first extension line meets the dimension line, as a fraction of page width (0–1).",
            ),
          start_y: z
            .number()
            .describe(
              "Where the dimension's first extension line meets the dimension line, as a fraction of page height (0–1).",
            ),
          end_x: z
            .number()
            .describe(
              'Where the second extension line meets the dimension line, as a fraction of page width (0–1).',
            ),
          end_y: z
            .number()
            .describe(
              'Where the second extension line meets the dimension line, as a fraction of page height (0–1).',
            ),
          length_mm: z
            .number()
            .describe(
              'The length printed on that span, in millimetres ("12000" → 12000; a length printed in metres, "12,0", → 12000).',
            ),
        })
        .strict(),
    )
    .describe(
      'Up to five lengths printed on this drawing (måttkedja, the distance between two grid axes): one row per span between two extension lines, with the number printed on it. Prefer the longest spans, spread over the drawing. Place the points on the extension lines, not on the number. Empty when the drawing prints no lengths. A level (+12.40) or a room area is not a length.',
    ),
});

export const drawingDetectionRootSchema = z.object({
  drawings: z
    .array(drawingDetectionItemSchema)
    .describe(
      'One row per distinct drawing. A sheet that shows one plan, section, or elevation is one row, even when the stamp is large or the drawing fills the page. Several drawings on one page are several rows. Empty only when the page has no plan, section, or elevation geometry. When the user message lists classifier blueprint segments, those page ranges must contribute rows here — do not return an empty array while those pages contain drawable plans, sections, or elevations. Do not invent a plan number the page does not print.',
    ),
});
export type DrawingDetectionRoot = z.infer<typeof drawingDetectionRootSchema>;
export type DrawingDetectionItem = z.infer<typeof drawingDetectionItemSchema>;

const toOpenAiStrictJsonSchema = (
  schema: z.ZodType,
): Record<string, unknown> => {
  const { $schema: _ignored, ...rest } = z.toJSONSchema(schema) as Record<
    string,
    unknown
  > & { $schema?: string };
  return rest;
};

export const buildDrawingDetectionRootJsonSchema = (): Record<
  string,
  unknown
> => toOpenAiStrictJsonSchema(drawingDetectionRootSchema);
